import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useViewport } from '../../../contexts/ViewportContext';
import type { Topic } from '../../../types/topic';
import { X } from 'lucide-react';
import { Loader } from '../../../components/ui';
import { logger } from '../../../utils/logger';
import { useToastUIStore } from '../../../store/toastUIStore';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';

// Import React Query hooks
import {
  useAddTableColumn,
  useAddTableRow,
  useDeleteTableColumn,
  useDeleteTableRow,
  useTableData,
  useUpdateTableCell,
  useUpdateTableColumn,
  useUpdateTableRow,
} from '../../../hooks/queries/useTables';
import { FormattedTableRow } from '../../../hooks/queries/tableTypes';
import { useUpdateTopic } from '../../../hooks/queries/useTopics';
import { useTopicSubscription } from '../../../hooks/useTopicSubscription';
import { tableKeys } from '../../../hooks/queries/tableKeys';
import { useInitialLoadingScreenManager } from '../../../hooks/useInitialLoadingScreenManager';

// Import components
import { ColumnTypeMenu } from './ColumnTypeMenu';
import { TableHeader } from './components/TableHeader';
import { TableContainer } from './components/TableContainer';

interface TableTopicProps {
  topic: Topic;
  hasCollaborators?: boolean;
  onBackClick: () => void;
}

export function TableTopic({ topic, onBackClick }: TableTopicProps) {
  const { isMobile } = useViewport();
  const horizontalScrollRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { addToast } = useToastUIStore();

  // React Query hooks
  const {
    data: tableData,
    isLoading,
    isError,
    error,
    refetch,
  } = useTableData(topic.id, { createIfMissing: true });

  // Extract data from tableData
  const tableDefinition = tableData?.tableDefinition || null;
  const columns = tableData?.columns || [];
  const rows = tableData?.rows || [];
  const cells = tableData?.cells || [];

  // Mutation hooks
  const updateTopicMutation = useUpdateTopic(); // Note: expects { topicId, updates } not { id, updates }
  const addColumnMutation = useAddTableColumn();
  const updateColumnMutation = useUpdateTableColumn();
  const deleteColumnMutation = useDeleteTableColumn();
  const addRowMutation = useAddTableRow();
  const updateRowMutation = useUpdateTableRow();
  const deleteRowMutation = useDeleteTableRow();
  const updateCellMutation = useUpdateTableCell();

  // Set up subscription for real-time updates
  // Set up real-time subscription for table data
  // Memoize subscriptions to prevent unnecessary re-subscriptions
  const subscriptions = useMemo(
    () => [
      { table: 'table_data' as const, queryKey: tableKeys.tableDataByTopic(topic.id) },
      { table: 'table_definitions' as const, queryKey: tableKeys.definitionsByTopic(topic.id) },
    ],
    [topic.id]
  );

  useTopicSubscription(topic.id, subscriptions);

  // State for sorting
  const [sorting, setSorting] = useState<SortingState>([]);

  // State for column being edited
  const [editingColumnId, setEditingColumnId] = useState<string | null>(null);
  const [showColumnTypeMenu, setShowColumnTypeMenu] = useState<string | null>(null);

  // Event listeners for column actions from the menu
  useEffect(() => {
    // Handle column deletion
    const handleDeleteColumn = (e: CustomEvent) => {
      const { columnId } = e.detail;
      if (columnId && tableDefinition?.id) {
        // Track this deletion to prevent duplicate processing

        // Delete the column
        deleteColumnMutation.mutate(columnId, {
          onSuccess: () => {
            // Update topic's last used timestamp
            updateTopicMutation.mutate({
              topicId: topic.id,
              updates: {
                last_used_at: new Date().toISOString(),
              },
            });
          },
          onError: error => {
            logger.error('Failed to delete column:', error);
            addToast({
              type: 'error',
              message: 'Failed to delete column',
              duration: 3000,
            });
          },
        });
      }
    };

    // Handle column type change blocked
    const handleColumnTypeChangeBlocked = (e: CustomEvent) => {
      const { columnName, fromType, toType } = e.detail;
      logger.component('Column type change blocked', { columnName, fromType, toType });

      let message;

      // Text to number
      if (fromType === 'text' && toType === 'number') {
        message = `Cannot change column "${columnName}" from text to number because it contains non-numeric values.`;
      }
      // Other conversions
      else {
        message = `Cannot change column "${columnName}" from ${fromType} to ${toType} because it contains incompatible data.`;
      }

      // Show toast notification to user
      addToast({
        type: 'info',
        message,
        duration: 5000,
      });
    };

    // Handle updating column options for select columns
    const handleUpdateColumnOptions = (e: CustomEvent) => {
      const { columnId, options } = e.detail;
      logger.component('Updating column options', {
        columnId,
        optionsCount: options.length,
        isArrayOfObjects: options.length > 0 && typeof options[0] === 'object',
      });

      if (columnId && tableDefinition?.id && Array.isArray(options)) {
        // Find the column to update
        const column = columns.find(col => col.id === columnId);

        if (column) {
          // Track this update to prevent duplicate processing

          // Update the column with new options
          updateColumnMutation.mutate(
            {
              columnId,
              updates: {
                options: { options },
              },
            },
            {
              onSuccess: () => {
                // Update topic's last used timestamp
                updateTopicMutation.mutate({
                  topicId: topic.id,
                  updates: {
                    last_used_at: new Date().toISOString(),
                  },
                });
              },
              onError: error => {
                logger.error('Failed to update column options:', error);
                addToast({
                  type: 'error',
                  message: 'Failed to update dropdown options',
                  duration: 3000,
                });
              },
            }
          );
        }
      }
    };

    // Handle column movement (left/right)
    const handleMoveColumn = (e: CustomEvent) => {
      const { columnId, direction } = e.detail;
      logger.component('Column move event received', { columnId, direction });

      if (columnId && tableDefinition?.id) {
        const columnIndex = columns.findIndex(col => col.id === columnId);
        logger.component('Found column at index', {
          columnIndex,
          columnId,
          columnCount: columns.length,
        });

        if (columnIndex === -1) {
          logger.error('Column not found for movement', { columnId });
          return;
        }

        const targetIndex = direction === 'left' ? columnIndex - 1 : columnIndex + 1;
        logger.component('Target column index', { targetIndex, direction });

        // Check if target index is valid
        if (targetIndex < 0 || targetIndex >= columns.length) {
          logger.error('Invalid target index for column movement', {
            targetIndex,
            columnsLength: columns.length,
          });
          return;
        }

        // Update the order of columns
        const movingColumn = { ...columns[columnIndex] };
        const targetColumn = { ...columns[targetIndex] };

        logger.component('Moving columns', {
          movingColumn: {
            id: movingColumn.id,
            name: movingColumn.name,
            orderIndex: movingColumn.orderIndex,
          },
          targetColumn: {
            id: targetColumn.id,
            name: targetColumn.name,
            orderIndex: targetColumn.orderIndex,
          },
        });

        // Swap order_index values
        const temp = movingColumn.orderIndex;
        movingColumn.orderIndex = targetColumn.orderIndex;
        targetColumn.orderIndex = temp;

        logger.component('After swap', {
          movingColumn: {
            id: movingColumn.id,
            name: movingColumn.name,
            orderIndex: movingColumn.orderIndex,
          },
          targetColumn: {
            id: targetColumn.id,
            name: targetColumn.name,
            orderIndex: targetColumn.orderIndex,
          },
        });

        // Track both updates to prevent duplicate processing

        // Update both columns
        updateColumnMutation.mutate(
          {
            columnId: movingColumn.id,
            updates: {
              orderIndex: movingColumn.orderIndex,
            },
          },
          {
            onSuccess: () => {
              logger.component('Updated moving column successfully', { columnId: movingColumn.id });

              // Update topic's last used timestamp
              updateTopicMutation.mutate({
                topicId: topic.id,
                updates: {
                  last_used_at: new Date().toISOString(),
                },
              });
            },
            onError: error => {
              logger.error('Failed to update moving column', { error, columnId: movingColumn.id });
            },
          }
        );

        updateColumnMutation.mutate(
          {
            columnId: targetColumn.id,
            updates: {
              orderIndex: targetColumn.orderIndex,
            },
          },
          {
            onSuccess: () => {
              logger.component('Updated target column successfully', { columnId: targetColumn.id });
            },
            onError: error => {
              logger.error('Failed to update target column', { error, columnId: targetColumn.id });
            },
          }
        );
      } else {
        logger.error('Missing required data for column movement', {
          columnId,
          hasTableDefinition: !!tableDefinition,
        });
      }
    };

    document.addEventListener('delete-column', handleDeleteColumn as EventListener);
    document.addEventListener('move-column', handleMoveColumn as EventListener);
    document.addEventListener(
      'column-type-change-blocked',
      handleColumnTypeChangeBlocked as EventListener
    );
    document.addEventListener('update-column-options', handleUpdateColumnOptions as EventListener);

    return () => {
      document.removeEventListener('delete-column', handleDeleteColumn as EventListener);
      document.removeEventListener('move-column', handleMoveColumn as EventListener);
      document.removeEventListener(
        'column-type-change-blocked',
        handleColumnTypeChangeBlocked as EventListener
      );
      document.removeEventListener(
        'update-column-options',
        handleUpdateColumnOptions as EventListener
      );
    };
  }, [
    tableDefinition,
    columns,
    deleteColumnMutation,
    updateColumnMutation,
    updateTopicMutation,
    addToast,
    topic.id,
  ]);

  // State for cell being edited
  const [editingCell, setEditingCell] = useState<{ rowId: string; columnId: string } | null>(null);

  // State for dropdown menu
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);

  // State for cell menu (for mobile)
  const [cellMenuOpen, setCellMenuOpen] = useState<{ rowId: string; columnId: string } | null>(
    null
  );

  // Effect to close dropdowns when clicking outside
  useEffect(() => {
    if (!showDropdown && !showAddMenu) return;

    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }

      // Close add menu when clicking outside
      const target = event.target as HTMLElement;
      if (showAddMenu && !target.closest('.add-menu-container')) {
        setShowAddMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown, showAddMenu, isMobile]);

  // Close cell menu only when viewport changes, not when data changes
  useEffect(() => {
    if (cellMenuOpen && isMobile) {
      setCellMenuOpen(null);
    }
  }, [isMobile]);

  // Handle loading screen state
  const loadingScreenManager = useInitialLoadingScreenManager();

  // Using a ref to track if we've already marked complete to avoid infinite updates
  const hasMarkedCompleteRef = useRef(false);
  useEffect(() => {
    if (!isLoading && !hasMarkedCompleteRef.current) {
      // Mark route as complete when loading is finished - but only once
      hasMarkedCompleteRef.current = true;
      loadingScreenManager.markRouteComplete();
    }
  }, [isLoading]); // Remove loadingScreenManager from deps to prevent infinite updates

  // Parse value based on column type
  const parseValueByType = (value: string, type: string) => {
    if (!value) return value;

    switch (type) {
      case 'number':
        return parseFloat(value);
      case 'boolean':
        return value.toLowerCase() === 'true';
      case 'date':
        return value ? new Date(value) : '';
      default:
        return value;
    }
  };

  // Prepare data for TanStack Table
  const tableReactData = useMemo(() => {
    // First sort rows by order_index to ensure consistent display order
    const sortedRows = [...rows].sort((a, b) => a.orderIndex - b.orderIndex);

    // Map rows with their cells
    return sortedRows.map(row => {
      const rowData: Record<string, unknown> = {
        id: row.id,
        order_index: row.orderIndex, // Include order_index in row data
      };

      // Add cells for this row
      columns.forEach(column => {
        const cell = cells.find(c => c.rowId === row.id && c.columnId === column.id);
        // Parse cell value based on column type
        rowData[column.id] = parseValueByType(cell?.value || '', column.type);
      });

      return rowData;
    });
  }, [rows, columns, cells]);

  // Format value for display based on column type
  const formatValueForDisplay = (value: unknown, type: string) => {
    if (value === null || value === undefined) return '';

    switch (type) {
      case 'date':
        return value instanceof Date ? value.toLocaleDateString() : '';
      case 'boolean':
        return value ? '✓' : '';
      case 'number': {
        // Format number with thousands separators and smart decimal places
        const numValue = parseFloat(String(value));
        if (isNaN(numValue)) return '';

        // Format with space as thousands separator and appropriate decimal places
        return Number.isInteger(numValue)
          ? numValue.toLocaleString(undefined, { useGrouping: true }).replace(/,/g, ' ') // No decimal places for integers
          : numValue
              .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              .replace(/,/g, ' ');
      }

      default:
        return String(value);
    }
  };

  // Create columns for TanStack Table
  const tableColumns = useMemo(() => {
    return columns.map(column => ({
      id: column.id,
      accessorKey: column.id,
      // Responsive sizing - fixed on mobile, flexible on desktop
      size: isMobile
        ? column.type === 'boolean'
          ? 80
          : column.type === 'number'
            ? 100
            : 200
        : column.type === 'boolean'
          ? 60
          : column.type === 'number'
            ? 80
            : 150,
      // Only for desktop - allow text columns to grow
      minSize: isMobile
        ? undefined
        : column.type === 'boolean'
          ? 60
          : column.type === 'number'
            ? 80
            : 120,
      maxSize: isMobile
        ? undefined
        : column.type === 'boolean'
          ? 100
          : column.type === 'number'
            ? 150
            : 300,
    }));
  }, [columns, isMobile]);

  // Setup TanStack Table
  const table = useReactTable({
    data: tableReactData,
    columns: tableColumns,
    state: {
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    // Add initial sorting by order_index if no user-specified sorting is active
    initialState: {
      sorting: [{ id: 'order_index', desc: false }],
    },
    defaultCanSort: false, // Disable automatic sorting on columns unless explicitly enabled
  });

  // Effect to handle viewport changes
  const [viewportMode, setViewportMode] = useState<'mobile' | 'desktop'>(
    isMobile ? 'mobile' : 'desktop'
  );

  useEffect(() => {
    const currentMode = isMobile ? 'mobile' : 'desktop';
    if (viewportMode !== currentMode) {
      logger.component('Viewport mode changed in TableTopic', {
        from: viewportMode,
        to: currentMode,
      });

      // Force reset scroll position
      if (horizontalScrollRef.current) {
        horizontalScrollRef.current.scrollLeft = 0;
      }

      // Update viewport mode
      setViewportMode(currentMode);

      // Force table to recompute
      table.resetRowSelection();
      table.resetColumnFilters();
      table.resetColumnOrder();
    }
  }, [isMobile, viewportMode, table, horizontalScrollRef]);

  // Handler for adding a new column
  const handleAddColumnAfter = useCallback(async () => {
    if (!tableDefinition?.id) return;

    try {
      // Calculate last position - just add a large gap to ensure it goes at the end
      const sortedColumns = [...columns].sort((a, b) => a.orderIndex - b.orderIndex);
      const lastPosition =
        sortedColumns.length > 0 ? sortedColumns[sortedColumns.length - 1].orderIndex + 1000 : 1000;

      // Generate a column name
      const columnName = `Column ${columns.length + 1}`;

      logger.component('Adding new column at the end', {
        lastPosition,
        columnCount: sortedColumns.length,
      });

      // Create the new column
      addColumnMutation.mutate(
        {
          tableDefinitionId: tableDefinition.id,
          name: columnName,
          type: 'text',
          orderIndex: lastPosition,
          isVisible: true,
        },
        {
          onSuccess: () => {
            // Update topic's last used timestamp
            updateTopicMutation.mutate({
              topicId: topic.id,
              updates: {
                last_used_at: new Date().toISOString(),
              },
            });
          },
          onError: error => {
            logger.error('Error in handleAddColumnAfter', {
              error: error instanceof Error ? error.message : String(error),
              tableId: tableDefinition.id,
            });
            addToast({
              type: 'error',
              message: 'Failed to add column',
              duration: 3000,
            });
          },
        }
      );
    } catch (error) {
      logger.error('Error in handleAddColumnAfter', {
        error: error instanceof Error ? error.message : String(error),
        tableId: tableDefinition?.id,
      });
    }
  }, [tableDefinition?.id, columns, addColumnMutation, updateTopicMutation, topic.id, addToast]);

  // Handler for adding a new row
  const handleAddRow = useCallback(() => {
    if (!tableDefinition?.id) return;

    addRowMutation.mutate(
      {
        tableDefinitionId: tableDefinition.id,
        orderIndex: rows.length,
      },
      {
        onSuccess: () => {
          // Update topic's last used timestamp
          updateTopicMutation.mutate({
            topicId: topic.id,
            updates: {
              last_used_at: new Date().toISOString(),
            },
          });
        },
        onError: error => {
          logger.error('Failed to add row:', error);
          addToast({
            type: 'error',
            message: 'Failed to add row',
            duration: 3000,
          });
        },
      }
    );
  }, [tableDefinition?.id, rows.length, addRowMutation, updateTopicMutation, topic.id, addToast]);

  // Update cell handler
  const handleUpdateCell = useCallback(
    (rowId: string, columnId: string, value: string) => {
      updateCellMutation.mutate(
        {
          rowId,
          columnId,
          value,
        },
        {
          onSuccess: () => {
            // Update topic's last used timestamp
            updateTopicMutation.mutate({
              topicId: topic.id,
              updates: {
                last_used_at: new Date().toISOString(),
              },
            });
          },
          onError: error => {
            logger.error('Failed to update cell:', error);
            addToast({
              type: 'error',
              message: 'Failed to update cell value',
              duration: 3000,
            });
          },
        }
      );
    },
    [updateCellMutation, updateTopicMutation, topic.id, addToast]
  );

  // Delete row handler
  const handleDeleteRow = useCallback(
    (tableDefId: string, rowId: string) => {
      // Track this deletion to prevent duplicate processing

      deleteRowMutation.mutate(rowId, {
        onSuccess: () => {
          // Update topic's last used timestamp
          updateTopicMutation.mutate({
            topicId: topic.id,
            updates: {
              last_used_at: new Date().toISOString(),
            },
          });
        },
        onError: error => {
          logger.error('Failed to delete row:', error);
          addToast({
            type: 'error',
            message: 'Failed to delete row',
            duration: 3000,
          });
        },
      });
    },
    [deleteRowMutation, updateTopicMutation, topic.id, addToast]
  );

  // Update row handler
  const handleUpdateRow = useCallback(
    (tableDefId: string, rowId: string, updates: Partial<FormattedTableRow>) => {
      updateRowMutation.mutate(
        {
          rowId,
          updates,
        },
        {
          onSuccess: () => {
            // Update topic's last used timestamp
            updateTopicMutation.mutate({
              topicId: topic.id,
              updates: {
                last_used_at: new Date().toISOString(),
              },
            });
          },
          onError: error => {
            logger.error('Failed to update row:', error);
            addToast({
              type: 'error',
              message: 'Failed to update row',
              duration: 3000,
            });
          },
        }
      );
    },
    [updateRowMutation, updateTopicMutation, topic.id, addToast]
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <Loader color="text-blue-500" className="mb-4" />
          <p className="text-gray-500">Loading table data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center text-center">
          <div className="text-red-500 mb-4">
            <X size={48} />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error loading table</h3>
          <p className="text-gray-500 max-w-md mb-4">
            {error instanceof Error ? error.message : 'An unknown error occurred'}
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // If no tableDefinition and not loading/error, we're still initializing
  if (!tableDefinition) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <Loader color="text-blue-500" className="mb-4" />
          <p className="text-gray-500">Initializing table...</p>
        </div>
      </div>
    );
  }

  // Main table view
  return (
    <div
      className="flex flex-col h-full max-h-full relative w-full"
      style={{
        height: isMobile ? '100vh' : '100%',
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden',
        overflowY: 'hidden', // Hide vertical scrollbar at container level
      }}
    >
      {/* Mobile header with dropdown menu */}
      {isMobile && (
        <TableHeader
          topic={topic}
          onBackClick={onBackClick}
          showDropdown={showDropdown}
          setShowDropdown={setShowDropdown}
          handleAddRow={handleAddRow}
          dropdownRef={dropdownRef}
        />
      )}

      {/* Table container - add flex-1 to fill available space */}
      <div className="flex-1 overflow-hidden">
        <TableContainer
          key={`table-container-${isMobile ? 'mobile' : 'desktop'}`} // Force re-render on viewport change
          horizontalScrollRef={horizontalScrollRef}
          isMobile={isMobile}
          table={table}
          columns={columns}
          rows={rows}
          cells={cells}
          tableDefinition={tableDefinition}
          editingColumnId={editingColumnId}
          setEditingColumnId={setEditingColumnId}
          showColumnTypeMenu={showColumnTypeMenu}
          setShowColumnTypeMenu={setShowColumnTypeMenu}
          editingCell={editingCell}
          setEditingCell={setEditingCell}
          cellMenuOpen={cellMenuOpen}
          setCellMenuOpen={setCellMenuOpen}
          updateColumn={(tableDefId, columnId, updates) => {
            // Track this update to prevent duplicate processing

            // Update the column with the new value
            updateColumnMutation.mutate(
              {
                columnId,
                updates,
              },
              {
                onSuccess: () => {
                  // Update topic's last used timestamp
                  updateTopicMutation.mutate({
                    topicId: topic.id,
                    updates: {
                      last_used_at: new Date().toISOString(),
                    },
                  });
                },
                onError: error => {
                  logger.error('Failed to update column:', error);
                  addToast({
                    type: 'error',
                    message: 'Failed to update column',
                    duration: 3000,
                  });
                },
              }
            );
          }}
          deleteColumn={(tableDefId, columnId) => {
            deleteColumnMutation.mutate(columnId);
          }}
          deleteRow={handleDeleteRow}
          updateCell={handleUpdateCell}
          updateRow={handleUpdateRow}
          addRow={handleAddRow}
          formatValueForDisplay={formatValueForDisplay}
          ColumnTypeMenu={ColumnTypeMenu}
          onAddColumnAfter={handleAddColumnAfter}
          handleAddRow={handleAddRow}
        />
      </div>
    </div>
  );
}
